var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
const MOBILE_MEDIA_QUERY = "(max-width: 991.98px)";
const STATIC_FILL_ATTRIBUTE = "data-sticky-static-fill";
const STATIC_FILL_REJECTED_ATTRIBUTE = "data-sticky-static-fill-rejected";
const STATIC_FILL_STATE_EVENT = "sticky-viewport:consumer-state-change";
const STATIC_FILL_TOLERANCE = 1;
const MEDIA_HEIGHT_COMPENSATION_ATTRIBUTE = "data-media-height-compensation";
const MEDIA_HEIGHT_COMPENSATION_PROPERTY = "--sticky-content-media-height-compensation";
const parseCssLengthToPixels = (value, rootFontSize = 16) => {
  const normalized = value.trim().toLowerCase();
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed)) return null;
  if (normalized.endsWith("rem") || normalized.endsWith("em")) {
    return parsed * rootFontSize;
  }
  if (normalized.endsWith("px") || /^-?\d*\.?\d+$/.test(normalized)) {
    return parsed;
  }
  return null;
};
class StickyContentBlockComponent extends HTMLElement {
  constructor() {
    super();
    __publicField(this, "_handleBreakpointChange", () => {
      this._syncMiddleMeasurement();
      this._requestEqualFullSync();
      this._syncMediaHeightCompensation();
    });
    __publicField(this, "_updateMediaHeightCompensation", (entries = []) => {
      if (!this.isConnected || this._mobileMedia.matches || !this.hasAttribute(MEDIA_HEIGHT_COMPENSATION_ATTRIBUTE)) {
        this._stopMediaHeightCompensation();
        return;
      }
      if (this.hasAttribute(STATIC_FILL_ATTRIBUTE)) {
        this._publishMediaHeightCompensation(1);
        return;
      }
      const entry = entries.find(({ target }) => target === this);
      const borderBox = Array.isArray(entry == null ? void 0 : entry.borderBoxSize) ? entry.borderBoxSize[0] : entry == null ? void 0 : entry.borderBoxSize;
      const currentHeight = (borderBox == null ? void 0 : borderBox.blockSize) ?? this.getBoundingClientRect().height;
      if (!Number.isFinite(currentHeight) || currentHeight <= 0) return;
      const viewportHeight = window.innerHeight;
      const viewportKey = `${Math.round(window.innerWidth)}:${Math.round(viewportHeight)}`;
      if (viewportKey !== this._mediaViewportKey) {
        const rootStyles = getComputedStyle(document.documentElement);
        const blockStyles = getComputedStyle(this);
        const rootFontSize = Number.parseFloat(rootStyles.fontSize) || 16;
        const pageMarginValue = blockStyles.getPropertyValue("--sticky-viewport-page-margin") || rootStyles.getPropertyValue("--page-margin");
        const sectionOffsetValue = blockStyles.getPropertyValue(
          "--sticky-viewport-section-offset"
        );
        const pageMargin = parseCssLengthToPixels(pageMarginValue, rootFontSize) ?? 0;
        const sectionOffset = parseCssLengthToPixels(sectionOffsetValue, rootFontSize) ?? 0;
        this._mediaReferenceHeight = Math.max(
          0,
          viewportHeight - pageMargin * 2 - sectionOffset
        );
        this._mediaViewportKey = viewportKey;
      }
      this._mediaReferenceHeight = Math.max(
        this._mediaReferenceHeight,
        currentHeight
      );
      this._publishMediaHeightCompensation(
        Math.max(1, this._mediaReferenceHeight / currentHeight)
      );
    });
    __publicField(this, "_evaluateEqualFullMeasurement", () => {
      var _a, _b;
      if (!this.isConnected || !((_a = this._equalFullMain) == null ? void 0 : _a.isConnected) || !((_b = this._equalFullProbe) == null ? void 0 : _b.isConnected)) {
        this._stopEqualFullMeasurement();
        return;
      }
      const targetHeight = this._equalFullProbe.getBoundingClientRect().height;
      const stickyHeight = this.getBoundingClientRect().height;
      const mainHeight = this._equalFullMain.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;
      const bothFitViewport = targetHeight > 0 && viewportHeight > 0 && stickyHeight <= viewportHeight + STATIC_FILL_TOLERANCE && mainHeight <= viewportHeight + STATIC_FILL_TOLERANCE;
      if (bothFitViewport) {
        this._setStaticFillCandidateRejected(false);
        this._setStaticFillState(true);
        return;
      }
      this._setStaticFillCandidateRejected(true);
      this._setStaticFillState(false);
    });
    this._mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
    this._middleResizeObserver = null;
    this._equalFullObserver = null;
    this._equalFullSyncRAF = null;
    this._equalFullContainer = null;
    this._equalFullMain = null;
    this._equalFullProbe = null;
    this._mediaResizeObserver = null;
    this._mediaReferenceHeight = 0;
    this._mediaViewportKey = "";
  }
  connectedCallback() {
    this._mobileMedia.addEventListener("change", this._handleBreakpointChange);
    this._syncMiddleMeasurement();
    this._requestEqualFullSync();
    this._syncMediaHeightCompensation();
  }
  disconnectedCallback() {
    this._mobileMedia.removeEventListener(
      "change",
      this._handleBreakpointChange
    );
    this._stopMiddleMeasurement();
    this._stopEqualFullMeasurement();
    this._stopMediaHeightCompensation();
  }
  attributeChangedCallback() {
    if (!this.isConnected) return;
    this._syncMiddleMeasurement();
    this._requestEqualFullSync();
    this._syncMediaHeightCompensation();
  }
  _syncMediaHeightCompensation() {
    const shouldCompensate = this.hasAttribute(MEDIA_HEIGHT_COMPENSATION_ATTRIBUTE) && this.dataset.stickyHeight === "full" && !this._mobileMedia.matches;
    if (!shouldCompensate || typeof ResizeObserver === "undefined") {
      this._stopMediaHeightCompensation();
      return;
    }
    if (!this._mediaResizeObserver) {
      this._mediaResizeObserver = new ResizeObserver(
        this._updateMediaHeightCompensation
      );
      this._mediaResizeObserver.observe(this);
    }
    this._updateMediaHeightCompensation();
  }
  _publishMediaHeightCompensation(scale) {
    const value = String(Math.round(scale * 1e4) / 1e4);
    if (this.style.getPropertyValue(MEDIA_HEIGHT_COMPENSATION_PROPERTY) !== value) {
      this.style.setProperty(MEDIA_HEIGHT_COMPENSATION_PROPERTY, value);
    }
  }
  _stopMediaHeightCompensation() {
    var _a;
    (_a = this._mediaResizeObserver) == null ? void 0 : _a.disconnect();
    this._mediaResizeObserver = null;
    this._mediaReferenceHeight = 0;
    this._mediaViewportKey = "";
    this.style.removeProperty(MEDIA_HEIGHT_COMPENSATION_PROPERTY);
  }
  _syncMiddleMeasurement() {
    const shouldMeasure = this.dataset.stickyAnchor === "middle" && !this._mobileMedia.matches;
    if (!shouldMeasure) {
      this._stopMiddleMeasurement();
      return;
    }
    if (this._middleResizeObserver || typeof ResizeObserver === "undefined")
      return;
    this._middleResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const borderBox = Array.isArray(entry == null ? void 0 : entry.borderBoxSize) ? entry.borderBoxSize[0] : entry == null ? void 0 : entry.borderBoxSize;
      const height = (borderBox == null ? void 0 : borderBox.blockSize) ?? this.getBoundingClientRect().height;
      this._publishMiddleHeight(height);
    });
    this._middleResizeObserver.observe(this);
  }
  _publishMiddleHeight(height) {
    if (!Number.isFinite(height) || height <= 0) return;
    const value = `${Math.ceil(height * 100) / 100}px`;
    if (this.style.getPropertyValue("--sticky-anchor-height") !== value) {
      this.style.setProperty("--sticky-anchor-height", value);
    }
    if (!this.hasAttribute("data-sticky-anchor-ready")) {
      this.setAttribute("data-sticky-anchor-ready", "");
    }
  }
  _stopMiddleMeasurement() {
    var _a;
    (_a = this._middleResizeObserver) == null ? void 0 : _a.disconnect();
    this._middleResizeObserver = null;
    this.style.removeProperty("--sticky-anchor-height");
    this.removeAttribute("data-sticky-anchor-ready");
  }
  _requestEqualFullSync() {
    var _a;
    const owner = this.classList.contains("sticky-content-block--sticky") ? this : (_a = this.closest(".sc-sticky-content-content-inner")) == null ? void 0 : _a.querySelector(
      ".sticky-content-block--sticky"
    );
    owner == null ? void 0 : owner._scheduleEqualFullSync();
  }
  _scheduleEqualFullSync() {
    if (this._equalFullSyncRAF) cancelAnimationFrame(this._equalFullSyncRAF);
    this._equalFullSyncRAF = requestAnimationFrame(() => {
      this._equalFullSyncRAF = null;
      this._syncEqualFullMeasurement();
    });
  }
  _syncEqualFullMeasurement() {
    if (!this.classList.contains("sticky-content-block--sticky")) return;
    const container = this.closest(".sc-sticky-content-content-inner");
    const main = container == null ? void 0 : container.querySelector(".sticky-content-block--main");
    const probe = container == null ? void 0 : container.querySelector(".sticky-content-static-fill-probe");
    const isEqualFullCandidate = !this._mobileMedia.matches && this.dataset.stickyHeight === "full" && (main == null ? void 0 : main.dataset.mainHeight) === "full" && probe;
    if (!isEqualFullCandidate) {
      this._stopEqualFullMeasurement();
      return;
    }
    if (typeof ResizeObserver === "undefined") {
      this._stopEqualFullMeasurement();
      this._equalFullContainer = container;
      this._setStaticFillCandidateRejected(true);
      return;
    }
    if (this._equalFullObserver && this._equalFullMain === main && this._equalFullProbe === probe) {
      this._beginEqualFullProbe();
      return;
    }
    this._stopEqualFullMeasurement();
    this._equalFullContainer = container;
    this._equalFullMain = main;
    this._equalFullProbe = probe;
    this._equalFullObserver = new ResizeObserver(
      this._evaluateEqualFullMeasurement
    );
    this._equalFullObserver.observe(this);
    this._equalFullObserver.observe(main);
    this._equalFullObserver.observe(probe);
    this._beginEqualFullProbe();
  }
  _beginEqualFullProbe() {
    if (this._equalFullProbe.hidden) this._equalFullProbe.hidden = false;
    this._setStaticFillCandidateRejected(false);
    this._setStaticFillState(true);
  }
  _setStaticFillCandidateRejected(rejected) {
    if (!this._equalFullContainer) return;
    if (rejected) {
      if (!this._equalFullContainer.hasAttribute(STATIC_FILL_REJECTED_ATTRIBUTE)) {
        this._equalFullContainer.setAttribute(
          STATIC_FILL_REJECTED_ATTRIBUTE,
          ""
        );
      }
    } else {
      if (this._equalFullContainer.hasAttribute(STATIC_FILL_REJECTED_ATTRIBUTE)) {
        this._equalFullContainer.removeAttribute(
          STATIC_FILL_REJECTED_ATTRIBUTE
        );
      }
    }
  }
  _setStaticFillState(active) {
    const blocks = [
      this,
      this._equalFullMain
    ].filter(Boolean);
    const changed = blocks.some(
      (block) => block.hasAttribute(STATIC_FILL_ATTRIBUTE) !== active
    );
    if (!changed) return;
    blocks.forEach((block) => {
      if (block.hasAttribute(STATIC_FILL_ATTRIBUTE) === active) return;
      block.toggleAttribute(STATIC_FILL_ATTRIBUTE, active);
    });
    this._updateMediaHeightCompensation();
    window.dispatchEvent(new CustomEvent(STATIC_FILL_STATE_EVENT));
  }
  _stopEqualFullMeasurement() {
    var _a;
    if (this._equalFullSyncRAF) cancelAnimationFrame(this._equalFullSyncRAF);
    this._equalFullSyncRAF = null;
    (_a = this._equalFullObserver) == null ? void 0 : _a.disconnect();
    this._equalFullObserver = null;
    this._setStaticFillState(false);
    if (this._equalFullProbe && !this._equalFullProbe.hidden) {
      this._equalFullProbe.hidden = true;
    }
    this._setStaticFillCandidateRejected(false);
    this._equalFullContainer = null;
    this._equalFullMain = null;
    this._equalFullProbe = null;
  }
}
__publicField(StickyContentBlockComponent, "observedAttributes", [
  "data-main-height",
  "data-sticky-anchor",
  "data-sticky-height"
]);
if (!customElements.get("sticky-content-block-component")) {
  customElements.define(
    "sticky-content-block-component",
    StickyContentBlockComponent
  );
}
